//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// team-picker imports
import { IContainer, IProps, IState } from "./TeamPickerWithActions.d";
import { styles } from "./TeamPickerWithActions.styles";
import { teamPickerWithActionsFragments } from "./TeamPickerWithActions.gql";
// state
import { IReduxState } from "@reducers/root.reducer";
// material-ui
import {
  FormControl,
  Select,
  ListSubheader,
  List,
  ListItem,
  Divider,
  MenuItem,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { colors } from "@clockwise/web-commons/src/styles/color.styles";

import { Add, PersonAdd, Settings, Check } from "@material-ui/icons";
import { TrackingEvents, track } from "@clockwise/web-commons/src/util/analytics.util";

const actionsEnum = {
  manage: "MANAGE",
  join: "JOIN",
  create: "CREATE",
};

//////////////////
// COMPONENT
//////////////////

class TeamPickerWithActionsCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      selectedTeamId: props.teamId,
      isMenuOpen: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.teamId !== this.state.selectedTeamId) {
      this.setState({ selectedTeamId: nextProps.teamId });
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers;
  // ~-~-~-~-~-~-~-
  private onChangeTeam = (selectedTeamId: string) => {
    if (!!selectedTeamId) {
      this.props.onChange(selectedTeamId);
      this.setState({ selectedTeamId });
    }
  };

  private getSelectedTeamName = (teamId: string) => {
    const { userTeams } = this.props;
    const selectedTeam = userTeams.list.find((team) => team.teamId === teamId);
    return !!selectedTeam ? selectedTeam.teamName : "";
  };

  private onClickAction = (action: string) => {
    const { selectedTeamId } = this.state;
    switch (action) {
      case actionsEnum.manage:
        track(TrackingEvents.TEAM_HEALTH.TEAMS_DROPDOWN_MANAGE_TEAM_SELECTED);
        return this.props.openTeamsDialog(selectedTeamId);
      case actionsEnum.join:
        track(TrackingEvents.TEAM_HEALTH.TEAMS_DROPDOWN_JOIN_TEAM_SELECTED);
        return this.props.openTeamsDialog(selectedTeamId, "join");
      case actionsEnum.create:
        track(TrackingEvents.TEAM_HEALTH.TEAMS_DROPDOWN_CREATE_TEAM_SELECTED);
        return this.props.openTeamsDialog(selectedTeamId, "create");
      default:
        return null;
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderMenuItem = (team: ISchema.ITeam) => {
    const { classes, primaryTeamId } = this.props;

    const isSelected = team.teamId === this.state.selectedTeamId;

    return (
      <ListItem
        cw-id="team-in-teampicker"
        key={`team-select-${team.teamId}-${team.teamName}`}
        value={team.teamId}
        className={isSelected ? classes.actionMenuItem : classes.listItem}
        onClick={() => this.onChangeTeam(team.teamId)}
      >
        {isSelected && (
          <Check className={classes.actionIcons} style={{ color: colors.green.darker }} />
        )}
        <div
          className={isSelected ? classes.actionLabel : classes.menuItemLabel}
          style={{ color: isSelected ? colors.green.darker : "inherit" }}
        >
          {team.teamName} {team.teamId === primaryTeamId && "(primary)"}
        </div>
      </ListItem>
    );
  };

  private renderActionItems = () => {
    const { classes } = this.props;
    return (
      <div className={classes.actionSection}>
        <ListItem
          className={classes.actionMenuItem}
          onClick={() => this.onClickAction(actionsEnum.manage)}
        >
          <Settings className={classes.actionIcons} />
          <div className={classes.actionLabel}>Manage your teams</div>
        </ListItem>
        <ListItem
          className={classes.actionMenuItem}
          onClick={() => this.onClickAction(actionsEnum.join)}
        >
          <PersonAdd className={classes.actionIcons} />
          <div className={classes.actionLabel}>Join an existing team</div>
        </ListItem>
        <ListItem
          className={classes.actionMenuItem}
          onClick={() => this.onClickAction(actionsEnum.create)}
        >
          <Add className={classes.actionIcons} />
          <div className={classes.actionLabel}>Create a new team</div>
        </ListItem>
      </div>
    );
  };

  public render() {
    const { userTeams, primaryTeamId, disabled, classes, disableUnderline } = this.props;
    const { selectedTeamId } = this.state;

    const sortedList = userTeams.list
      .map((a) => a)
      .sort((a, b) => {
        if (a.teamId === primaryTeamId) {
          return -1;
        } else if (b.teamId === primaryTeamId) {
          return 1;
        } else {
          return a.teamName.localeCompare(b.teamName);
        }
      });

    return (
      <FormControl className={this.props.classes.container} fullWidth>
        <Select
          value={selectedTeamId}
          onOpen={() => this.setState({ isMenuOpen: true })}
          onClose={() => this.setState({ isMenuOpen: false })}
          name="Team Picker"
          inputProps={{
            name: "Team Picker",
            id: "cw-team-select",
          }}
          MenuProps={{
            classes: {
              paper: this.props.classes.menuPaper,
            },
          }}
          fullWidth
          disabled={disabled}
          classes={{
            select: classes.inlineSelect,
          }}
          disableUnderline={disableUnderline}
        >
          {!this.state.isMenuOpen && (
            <MenuItem value={this.state.selectedTeamId}>
              {this.getSelectedTeamName(this.state.selectedTeamId || "")}
            </MenuItem>
          )}
          <List classes={{ root: classes.teamList }}>
            {sortedList.length > 0 && (
              <ListSubheader className={classes.selectSubHeader}>Your Teams </ListSubheader>
            )}
            {sortedList.map(this.renderMenuItem)}
            <Divider className={classes.divider} />
            {this.renderActionItems()}
          </List>
        </Select>
      </FormControl>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const TeamPickerStyled = withStyles(styles)(TeamPickerWithActionsCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const TeamPickerRedux = connect(mapStateToProps, {})(TeamPickerStyled);

//////////////////
// RELAY
//////////////////
export const TeamPickerWithActions = createFragmentContainer<IContainer>(
  TeamPickerRedux,
  teamPickerWithActionsFragments,
);
